import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {POLYMER_SUBTYPE_TO_MODEL_TYPE, PolymerSubtype} from 'constants/MolbioConstants';
import useFilteredCurrentlyOpenPolymerData from 'molbio/bulk-assembly/add-polymers/useFilteredCurrentlyOpenPolymerData';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import {GRAY_DARK} from 'ui-library/Colors';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import {typeToName} from 'util/NameUtil';

export interface AddPolymersMenuProps {
  /** Array of polymer types that can be used in the bulk assembly. */
  supportedPolymerTypes: Array<PolymerSubtype>;
  /** Callback for opening the add fragments by search modal */
  openAddPolymersSearchModal: () => void;
  /** Callback for opening the add fragments from worklists modal */
  openAddPolymersWorklistModal: () => void;
  /** Callback to open modal for adding a particular sequence */
  openAddPolymerModal: (sequenceId: string) => Promise<void>;
}

/**
 * A menu that launches various modals to add polymers to a bulk assembly workflow.
 */
export default function AddPolymersMenu({
  supportedPolymerTypes,
  openAddPolymersSearchModal,
  openAddPolymersWorklistModal,
  openAddPolymerModal,
}: AddPolymersMenuProps): JSX.Element {
  const currentlyOpenPolymerData = useFilteredCurrentlyOpenPolymerData(supportedPolymerTypes);
  const optionDataTestAttrs = {component: 'BulkAssemblyAddPolymersMenu', element: 'dropdown'};
  return (
    <Menu height={MenuHeight.MEDIUM} width={MenuWidth.MEDIUM}>
      <Section>
        <Option onSelect={noop} isDisabled={currentlyOpenPolymerData.length === 0}>
          Open sequences
          <Menu height={MenuHeight.MEDIUM} width={MenuWidth.MEDIUM}>
            {currentlyOpenPolymerData.map(({apiIdentifier, name, polymerSubtype}) => (
              <Option key={apiIdentifier} onSelect={openAddPolymerModal} value={apiIdentifier}>
                <div>{name}</div>
                <div className={css(styles.sequenceType)}>
                  {typeToName(POLYMER_SUBTYPE_TO_MODEL_TYPE[polymerSubtype])}
                </div>
              </Option>
            ))}
          </Menu>
        </Option>
        <Option onSelect={openAddPolymersSearchModal} dataTestAttrs={{...optionDataTestAttrs, key: 'search'}}>
          Search for sequences
        </Option>
      </Section>
      <Section>
        <Option
          onSelect={openAddPolymersWorklistModal}
          dataTestAttrs={{...optionDataTestAttrs, key: 'worklist'}}
        >
          Add from worklist
        </Option>
      </Section>
    </Menu>
  );
}

const styles = StyleSheet.create({
  sequenceType: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARK,
  },
});
